import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { MapPinIcon } from '@heroicons/react/24/outline';

/**
 * GeoDistributionChart - Bar chart showing geographic distribution
 */
const GeoDistributionChart = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="h-4 bg-gray-200 rounded w-40 mb-4 animate-pulse"></div>
        <div className="h-64 bg-gray-100 rounded animate-pulse"></div>
      </div>
    );
  }

  if (!data || !data.countries || data.countries.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-medium text-gray-700 mb-4 flex items-center gap-2">
          <MapPinIcon className="w-4 h-4" />
          Geographic Distribution
        </h3>
        <div className="h-64 flex items-center justify-center text-gray-500">
          No geographic data available
        </div>
      </div>
    );
  }

  // Prepare chart data - top 10 countries
  // API returns { code, name, count, percentage } for each country
  const chartData = data.countries
    .slice(0, 10)
    .map(item => ({
      country: item.name || item.country || item.code || 'Unknown',
      requests: item.count,
      percentage: item.percentage
    }));

  const totalRequests = data.totalRequests || chartData.reduce((sum, item) => sum + item.requests, 0);

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-gray-700 flex items-center gap-2">
          <MapPinIcon className="w-4 h-4" />
          Geographic Distribution
        </h3>
        <span className="text-xs text-gray-500">
          {data.uniqueCountries || data.countries.length} countries
        </span>
      </div>

      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 5, right: 30, left: 60, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
            <XAxis
              type="number"
              tick={{ fontSize: 11 }}
              stroke="#9ca3af"
              tickFormatter={(value) => value.toLocaleString()}
            />
            <YAxis
              type="category"
              dataKey="country"
              tick={{ fontSize: 11 }}
              stroke="#9ca3af"
              width={55}
            />
            <Tooltip
              formatter={(value) => [value.toLocaleString(), 'Requests']}
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                fontSize: '12px'
              }}
              labelFormatter={(label) => `Country: ${label}`}
            />
            <Bar
              dataKey="requests"
              fill="#6366f1"
              radius={[0, 4, 4, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Country list with percentages */}
      <div className="mt-4 max-h-32 overflow-y-auto">
        <div className="grid grid-cols-2 gap-2 text-xs">
          {chartData.map((item, index) => (
            <div
              key={index}
              className="flex items-center justify-between bg-gray-50 rounded px-2 py-1"
            >
              <span className="text-gray-700 truncate" title={item.country}>
                {item.country}
              </span>
              <span className="text-gray-500 ml-2">
                {((item.requests / totalRequests) * 100).toFixed(1)}%
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default GeoDistributionChart;
